import { Injectable, inject, InjectionToken, signal, computed, effect, untracked } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, NavigationStart, ActivatedRouteSnapshot } from '@angular/router';
import { filter, startWith, switchMap, Observable, from, merge } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { USER_ID_PROVIDER } from '../services/user-scoped-storage.service';
import type { BreadcrumbItem, BreadcrumbConfig, BreadcrumbResolvedItem } from './breadcrumb.types';

/**
 * Function type for resolving dynamic breadcrumb labels.
 * May return a plain string or a structured BreadcrumbResolvedItem that includes entity appearance.
 */
export type BreadcrumbLabelResolver = (
  params: Record<string, string>
) => Promise<string | BreadcrumbResolvedItem> | Observable<string | BreadcrumbResolvedItem> | string | BreadcrumbResolvedItem;

/**
 * Function type for loading sibling items for dropdown navigation.
 * Receives route params and current URL, returns sibling items.
 */
export type BreadcrumbSiblingsLoader = (params: Record<string, string>, currentUrl: string) => Observable<BreadcrumbItem[]>;

/**
 * Registry for label resolvers - provided by the application.
 */
export const BREADCRUMB_LABEL_RESOLVERS = new InjectionToken<Record<string, BreadcrumbLabelResolver>>('BREADCRUMB_LABEL_RESOLVERS');

/**
 * Registry for siblings loaders - provided by the application.
 */
export const BREADCRUMB_SIBLINGS_LOADERS = new InjectionToken<Record<string, BreadcrumbSiblingsLoader>>('BREADCRUMB_SIBLINGS_LOADERS');

/**
 * Service for generating breadcrumbs from route configuration.
 *
 * Automatically builds breadcrumb trails based on:
 * - Route data with `breadcrumb` configuration
 * - Dynamic parameter resolution via registered resolvers
 * - Sibling loading via registered loaders
 *
 * The application must provide resolvers and loaders:
 * ```typescript
 * providers: [
 *   {
 *     provide: BREADCRUMB_LABEL_RESOLVERS,
 *     useValue: {
 *       person: (params) => personService.getName(params['id']),
 *     }
 *   },
 *   {
 *     provide: BREADCRUMB_SIBLINGS_LOADERS,
 *     useValue: {
 *       persons: (params, url) => personService.list().pipe(map(...)),
 *     }
 *   }
 * ]
 * ```
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly labelResolvers = inject(BREADCRUMB_LABEL_RESOLVERS, {
    optional: true,
  });
  private readonly siblingsLoaders = inject(BREADCRUMB_SIBLINGS_LOADERS, {
    optional: true,
  });

  // Cache for resolved entity labels and appearance
  private readonly nameCache = new Map<string, BreadcrumbResolvedItem>();
  private readonly userIdFn = inject(USER_ID_PROVIDER, { optional: true });
  private readonly currentUserId = computed(() => this.userIdFn?.() ?? '__anon__');

  constructor() {
    let previousUserId: string | undefined;
    effect(() => {
      const userId = this.currentUserId();
      if (previousUserId === undefined) {
        previousUserId = userId;
        return;
      }
      if (previousUserId === userId) return;
      previousUserId = userId;
      untracked(() => this.clearCache());
    });
  }

  // Extra items provided by components (interactive dropdowns, date pickers, etc.)
  // Components are responsible for clearing their own items via ngOnDestroy
  private readonly _extraItems = signal<BreadcrumbItem[]>([]);

  // Route-based breadcrumbs (auto-generated from route config)
  private readonly _routeItems = toSignal(
    merge(this.router.events.pipe(filter(event => event instanceof NavigationEnd)), this.translate.onLangChange).pipe(
      startWith(null), // Trigger on initial load
      switchMap(() => from(this.buildBreadcrumbs()))
    ),
    { initialValue: [] as BreadcrumbItem[] }
  );

  // Clear extra items on navigation to a DIFFERENT route family.
  // Don't clear if staying within the same route family (e.g., planning routes).
  // This prevents flicker during internal navigation with auto-redirects.
  private readonly _clearExtrasOnNavigation = this.router.events
    .pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart),
      filter(event => {
        const currentUrl = this.router.url.split('?')[0];
        const targetUrl = event.url.split('?')[0];

        // Don't clear if same URL
        if (currentUrl === targetUrl) {
          return false;
        }

        // Don't clear if staying within the same route family
        // Route family is determined by the first two path segments (e.g., /planning/view, /planning/periods)
        const currentFamily = this.getRouteFamily(currentUrl);
        const targetFamily = this.getRouteFamily(targetUrl);

        // Only clear if actually leaving the route family
        return currentFamily !== targetFamily;
      })
    )
    .subscribe(() => this.clearExtraItems());

  /**
   * Get the route family from a URL path.
   * Returns the first two segments (e.g., "/planning/view" or "/employees").
   */
  private getRouteFamily(url: string): string {
    const segments = url.split('/').filter(Boolean);
    // Take first two segments as the family identifier
    return '/' + segments.slice(0, 2).join('/');
  }

  /**
   * Combined breadcrumbs: route-based items + extra items from components.
   * The last item is automatically marked as current.
   */
  readonly breadcrumbs = computed(() => {
    const routeItems = this._routeItems();
    const extraItems = this._extraItems();

    // If we have extra items, unmark isCurrent from route items
    // and mark the last extra item as current
    if (extraItems.length > 0) {
      const unmarkedRouteItems = routeItems.map(item => ({
        ...item,
        isCurrent: false,
      }));
      const markedExtraItems = extraItems.map((item, index) => ({
        ...item,
        isCurrent: index === extraItems.length - 1,
      }));
      return [...unmarkedRouteItems, ...markedExtraItems];
    }

    return routeItems;
  });

  /**
   * Set extra breadcrumb items to append after route-based items.
   * Use this to add interactive elements like dropdowns or date pickers.
   *
   * @example
   * ```typescript
   * breadcrumbService.setExtraItems([
   *   {
   *     type: 'month-picker',
   *     label: 'February 2026',
   *     date: new Date(),
   *     onDateChange: (d) => this.onMonthChange(d)
   *   },
   *   {
   *     type: 'dropdown',
   *     label: 'Select Team',
   *     options: teams,
   *     value: selectedTeamId,
   *     onSelect: (id) => this.onTeamChange(id)
   *   }
   * ]);
   * ```
   */
  setExtraItems(items: BreadcrumbItem[]): void {
    this._extraItems.set(items);
  }

  /**
   * Clear extra breadcrumb items.
   * Called automatically on navigation, but can be called manually.
   */
  clearExtraItems(): void {
    this._extraItems.set([]);
  }

  /**
   * Build breadcrumbs from the current route tree.
   */
  private async buildBreadcrumbs(): Promise<BreadcrumbItem[]> {
    const items: BreadcrumbItem[] = [];

    // Traverse route tree starting from root
    const snapshot = this.router.routerState.snapshot.root;
    await this.collectBreadcrumbs(snapshot, items);

    // Mark last item as current
    if (items.length > 0) {
      const lastItem = items.at(-1);
      if (lastItem) {
        lastItem.isCurrent = true;
      }
    }

    return items;
  }

  /**
   * Recursively collect breadcrumbs from route snapshots.
   * Tracks seen paths to prevent duplicates from index routes.
   */
  private async collectBreadcrumbs(
    snapshot: ActivatedRouteSnapshot,
    items: BreadcrumbItem[],
    pathSegments: string[] = [],
    seenPaths: Set<string> = new Set()
  ): Promise<void> {
    // Collect URL segments from this snapshot
    const newSegments = [...pathSegments, ...snapshot.url.map(s => s.path)];
    const currentPath = '/' + newSegments.filter(Boolean).join('/');

    // Check for breadcrumb configuration using the route's OWN config (not inherited)
    // This prevents duplicate breadcrumbs when child routes inherit parent config
    const config = snapshot.routeConfig?.data?.['breadcrumb'] as BreadcrumbConfig | undefined;

    // Only add if we have config and haven't seen this path yet
    // This prevents duplicates when index route inherits parent's config
    if (config && !seenPaths.has(currentPath)) {
      seenPaths.add(currentPath);
      const item = await this.configToItem(config, snapshot, newSegments);
      if (item) {
        items.push(item);
      }
    }

    // Process children
    for (const child of snapshot.children) {
      await this.collectBreadcrumbs(child, items, newSegments, seenPaths);
    }
  }

  /**
   * Convert breadcrumb config to a breadcrumb item.
   */
  private async configToItem(
    config: BreadcrumbConfig,
    snapshot: ActivatedRouteSnapshot,
    pathSegments: string[]
  ): Promise<BreadcrumbItem | null> {
    // Skip this breadcrumb if configured
    if (config.skip) {
      return null;
    }

    const label = config.label;

    // Resolve dynamic labels (and optional appearance) via registered resolver
    const item: BreadcrumbItem = { label };

    if (config.resolver && this.labelResolvers?.[config.resolver]) {
      const resolved = await this.resolveLabel(config.resolver, snapshot.params, this.labelResolvers[config.resolver]);
      item.label = resolved.label;
      if (!item.icon && resolved.icon) item.icon = resolved.icon;
      item.color = resolved.color ?? null;
      item.imageUrl = resolved.imageUrl ?? null;
    }

    // Static icon from route config overrides resolved icon
    if (config.icon) {
      item.icon = config.icon;
    }

    // Only add route if not inactive (inactive items are non-clickable text)
    if (!config.inactive) {
      item.route = ['/', ...pathSegments.filter(Boolean)];
    }

    // Add siblings loader if configured and registered
    if (config.siblingsLoader && this.siblingsLoaders?.[config.siblingsLoader]) {
      item.hasSiblings = true;
      const loaderFn = this.siblingsLoaders[config.siblingsLoader];
      item.siblingsLoader = () => loaderFn(snapshot.params, this.router.url.split('?')[0]);
    }

    return item;
  }

  /**
   * Resolve dynamic entity label and optional appearance via registered resolver.
   */
  private async resolveLabel(
    resolver: string,
    params: Record<string, string>,
    resolverFn: BreadcrumbLabelResolver
  ): Promise<BreadcrumbResolvedItem> {
    const cacheKey = `${resolver}:${this.translate.currentLang}:${JSON.stringify(params)}`;

    const cached = this.nameCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    // Default to ID if resolution fails
    let resolved: BreadcrumbResolvedItem = { label: params['id'] || 'Unknown' };

    try {
      const result = resolverFn(params);

      let raw: string | BreadcrumbResolvedItem;

      if (result instanceof Promise) {
        raw = await result;
      } else if (typeof result === 'string' || (result !== null && typeof result === 'object' && 'label' in result)) {
        raw = result as string | BreadcrumbResolvedItem;
      } else {
        // Observable
        raw = await new Promise<string | BreadcrumbResolvedItem>((resolve, reject) => {
          (result as Observable<string | BreadcrumbResolvedItem>).subscribe({
            next: val => resolve(val),
            error: (err: unknown) => reject(err),
          });
        });
      }

      resolved = typeof raw === 'string' ? { label: raw } : raw;
    } catch {
      // Use fallback resolved item
    }

    this.nameCache.set(cacheKey, resolved);
    return resolved;
  }

  /**
   * Clear the name cache (e.g., after entity update).
   */
  clearCache(): void {
    this.nameCache.clear();
  }

  /**
   * Clear a specific cache entry.
   */
  clearCacheEntry(resolver: string, id: string): void {
    const cacheKey = `${resolver}:{"id":"${id}"}`;
    this.nameCache.delete(cacheKey);
  }
}
